// Best-effort key inference from a Song.
//
// Walks every voice's effect chain looking for `.scale("C minor")` or
// `.chord("Am7")` arguments and tallies their roots. The most-frequent
// root wins; mode is taken from the dominant scale effect (or inferred
// from chord qualities — minor chord → minor mode).
//
// Confidence is the fraction of the winning root's votes over the
// total — 1.0 means every voice agrees, 0.4 means a plurality.
//
// Returns null when no scale/chord args were found at all. The
// agent's analyze_song tool surfaces this so the model can ask the
// user instead of guessing.

import {
  isConditionalEffect,
  type Effect,
  type Expression,
  type Song,
} from '../blocks/types';
import {
  parseChordString,
  parseScaleString,
} from '../blocks/tonal/parse';
import { isKnownMode, type KeyMode } from './keys';

export type InferredKey = {
  root: string;
  mode: KeyMode;
  /** 0..1 — fraction of votes the winning root received. */
  confidence: number;
  /** How many scale/chord args contributed to the inference. */
  sampleSize: number;
};

export function inferKey(song: Song): InferredKey | null {
  // Per-root mode tallies — picking mode globally lets a single
  // off-key chord stab override the dominant scale's mode. Anchoring
  // mode to the *winning* root's votes keeps the result coherent.
  const rootVotes = new Map<string, number>();
  const modesByRoot = new Map<string, Map<KeyMode, number>>();
  let total = 0;

  for (const stmt of song.statements) {
    if (stmt.kind !== 'const' && stmt.kind !== 'expression') continue;
    walkExpression(stmt.expr, (parsed) => {
      total += 1;
      rootVotes.set(parsed.root, (rootVotes.get(parsed.root) ?? 0) + 1);
      if (!parsed.mode) return;
      let inner = modesByRoot.get(parsed.root);
      if (!inner) {
        inner = new Map();
        modesByRoot.set(parsed.root, inner);
      }
      inner.set(parsed.mode, (inner.get(parsed.mode) ?? 0) + 1);
    });
  }

  if (total === 0) return null;

  // Pick the root with the most votes (ties broken by insertion order,
  // which mirrors statement order — usually chronological intent).
  let bestRoot: string | null = null;
  let bestRootCount = 0;
  for (const [root, count] of rootVotes) {
    if (count > bestRootCount) {
      bestRoot = root;
      bestRootCount = count;
    }
  }
  if (!bestRoot) return null;

  // Mode comes from the winning root's tally only. If that root never
  // got a mode vote (only ambiguous-quality chords like sus/dim),
  // default to major — the most common Western fallback.
  let bestMode: KeyMode = 'major';
  let bestModeCount = 0;
  const rootModes = modesByRoot.get(bestRoot);
  if (rootModes) {
    for (const [mode, count] of rootModes) {
      if (count > bestModeCount) {
        bestMode = mode;
        bestModeCount = count;
      }
    }
  }

  return {
    root: bestRoot,
    mode: bestMode,
    confidence: bestRootCount / total,
    sampleSize: total,
  };
}

type Vote = { root: string; mode?: KeyMode };

function walkExpression(expr: Expression, onVote: (v: Vote) => void): void {
  if (expr.kind === 'voice.s' || expr.kind === 'voice.note') {
    walkEffects(expr.effects, onVote);
    return;
  }
  if (expr.kind === 'ref') {
    if (expr.effects) walkEffects(expr.effects, onVote);
    return;
  }
  if (expr.kind === 'stack') {
    for (const c of expr.children) walkExpression(c, onVote);
    return;
  }
  if (expr.kind === 'arrange') {
    for (const e of expr.entries) walkExpression(e.section, onVote);
    return;
  }
}

function walkEffects(effects: Effect[], onVote: (v: Vote) => void): void {
  for (const eff of effects) {
    if (isConditionalEffect(eff)) {
      walkEffects(eff.body, onVote);
      continue;
    }
    if (eff.kind === 'scale' || eff.kind === 'chord') {
      const arg = eff.args[0];
      if (!arg || arg.kind !== 'string') continue;
      const parsed = parseSingleArg(eff.kind, arg.value);
      if (parsed) onVote(parsed);
    }
  }
}

function parseSingleArg(
  kind: 'scale' | 'chord',
  value: string,
): { root: string; mode?: KeyMode } | null {
  if (kind === 'scale') {
    const s = parseScaleString(value);
    if (!s) return null;
    return isKnownMode(s.mode) ? { root: s.root, mode: s.mode } : { root: s.root };
  }
  const c = parseChordString(value);
  if (!c) return null;
  // A chord's quality only weakly votes for a mode. Treat anything
  // starting with "m" (but not "maj") as a minor-mode signal; "maj7"
  // and "9" as a major-mode signal; everything else (sus, dim, aug)
  // is ambiguous and votes only for the root.
  const q = c.quality;
  if (q.startsWith('m') && !q.startsWith('maj')) {
    return { root: c.root, mode: 'minor' };
  }
  if (q === '' || q === 'maj7' || q === 'maj9' || q === '9') {
    return { root: c.root, mode: 'major' };
  }
  return { root: c.root };
}
